import React from 'react';
import { Provider } from 'react-redux';
import configureStore from '../store/findBobPublicStore';
import HomeContainer from './HomeContainer';
import GuidedTour from '../components/Shared/GuidedTour/GuidedTour';
import { ApolloProvider } from '@apollo/client';
import client from '../lib/apolloClient';

const HomePageContainer = (props, railsContext) => {
  // Ensure company.features/feature_types are present on the company object
  const mergedCompany = props.company
    ? { ...props.company, feature_types: (props.company.feature_types || props.features || []), features: (props.company.features || props.features || []) }
    : {
      host: railsContext && railsContext.company_host,
      features: (railsContext && railsContext.features) || [],
      file_import_disclaimer: railsContext && railsContext.file_import_disclaimer,
      name: railsContext && railsContext.company_name,
      branded_name: railsContext && railsContext.company_branded_name,
      short_name: railsContext && railsContext.company_short_name,
    };

  const initialState = {
    company: mergedCompany,
    user: props.current_user,
    intl: { locale: (railsContext && railsContext.i18nLocale) || 'en' },
  };

  const store = configureStore(initialState);
  if (typeof document !== 'undefined') {
    document.store = store;
  }

  return (
    <Provider store={store}>
      <ApolloProvider client={client}>
        <div className="bg-succession-bg-secondary min-h-screen">
          <HomeContainer {...props} />
          {(() => {
            // Intentionally no-op: we only auto-open Getting Started after the tour completes,
            // and only if it hasn't been opened before (see HomeTourGate.handleComplete)
          })()}
          <HomeTourGate
            shouldShowGettingStarted={(props && props.current_user && (props.current_user.should_show_getting_started === true || props.current_user['should_show_getting_started?'] === true))}
            userId={(props && props.current_user && props.current_user.id) || null}
          />
        </div>
      </ApolloProvider>
    </Provider>
  );
};

const HomeTourGate = ({ shouldShowGettingStarted = false, userId = null }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [externalSteps, setExternalSteps] = React.useState(null);
  const STORAGE_KEY = userId ? `succession_home_tour_shown_v1_${userId}` : 'succession_home_tour_shown_v1';

  React.useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window?.localStorage) {
        const hasSeen = window.localStorage.getItem(STORAGE_KEY) === '1';
        if (!hasSeen) setIsOpen(true);

        // Expose a simple test helper in the console
        window.resetSuccessionHomeTour = (openImmediately = true) => {
          try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) {}
          if (openImmediately) setIsOpen(true);
        };

        // Support query-string triggers: ?tour=reset | ?tour=open
        try {
          const params = new URLSearchParams(window.location.search || '');
          const tourAction = params.get('tour');
          if (tourAction === 'reset') {
            window.localStorage.removeItem(STORAGE_KEY);
            setIsOpen(true);
          } else if (tourAction === 'open') {
            setIsOpen(true);
          }
        } catch (_) {}
      }
    } catch (_) {}

    // Listen for programmatic tour open (post wizard)
    const onOpen = () => {
      try {
        const steps = window.__guidedTourSteps;
        if (Array.isArray(steps) && steps.length) {
          setExternalSteps(steps);
          setIsOpen(true);
        }
      } catch (_) { setIsOpen(true); }
    };
    window.addEventListener('open-guided-tour', onOpen);
    return () => { window.removeEventListener('open-guided-tour', onOpen); };
  }, []);

  const steps = [
    {
      id: 'explore',
      title: 'Explore',
      description: 'We’ve personalized this page for you based on your goals. Here’s where you’ll see your next best steps.',
      targetSelector: '[data-tutorial-id="explore-section"], [data-tutorial-id="explore-cards"]',
      position: 'bottom',
    },
    {
      id: 'graph-dropdown',
      title: 'Insights',
      description: 'Use this to switch between different quantitative views, such as Growth, Succession, and more.',
      targetSelector: '[data-tutorial-id="insights-column"], [data-tutorial-id="insights-dropdown"] select',
      position: 'right',
    },
    {
      id: 'activity',
      title: 'Your Activity',
      description: 'Your progress and recent activity will appear here, so you always know where you left off.',
      targetSelector: '[data-tutorial-id="activity-feed"]',
      position: 'left',
    },
    {
      id: 'app-launcher',
      title: 'All Tools',
      description: 'When you’re ready, you can explore all available tools from here.',
      targetSelector: '[data-tutorial-id="app-launcher-button"]',
      position: 'bottom',
      onEnter: () => {
        try {
          const btn = document.querySelector('[data-tutorial-id="app-launcher-button"]');
          const panel = document.querySelector('[data-tutorial-id="app-launcher-panel"]');
          const alreadyOpen = Boolean(panel);
          // Prevent rapid toggling if onEnter runs multiple times
          if (!alreadyOpen && btn && !window.__tourAppLauncherOpenedOnce) {
            window.__tourAppLauncherOpenedOnce = true;
            btn.click();
          }
        } catch (_) {}
      }
    },
  ];

  const handleComplete = () => {
    try { if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, '1'); } catch (_) {}
    try {
      if (shouldShowGettingStarted && typeof window !== 'undefined') {
        const key = 'getting_started_opened_once';
        const already = window.localStorage.getItem(key) === '1';
        if (!already) {
          setTimeout(() => { window.dispatchEvent(new Event('open-getting-started')); window.localStorage.setItem(key, '1'); }, 50);
        }
      }
    } catch (_) {}
  };

  if (!isOpen) return null;
  return (
    <GuidedTour
      isOpen={isOpen}
      steps={externalSteps || steps}
      onClose={() => setIsOpen(false)}
      onComplete={() => { handleComplete(); setIsOpen(false); }}
      onReplayRequest={() => setIsOpen(true)}
    />
  );
};

export default HomePageContainer;


